import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import Crane from "icons/crane.svg";
import MessageSent from "icons/message_sent.svg";
import validator from "validator";
import { useSelector } from "react-redux";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
    pl: "0.5rem",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const TEXT = {
  contact: {
    title: "contact_us_page.title",
    subtitle: "landing_recruiter.contact_form.your_question",
    messageLabel: "landing_candidate.contact_form.your_question",
    success: "landing_candidate.contact_form.contact_success",
  },
  bug_report: {
    title: "bug_report_page.title",
    subtitle: "bug_report_page.subtitle",
    messageLabel: "bug_report_page.message_label",
    success: "bug_report_page.success",
  },
};

const ContactUs = ({ variant = "contact" }) => {
  const text = TEXT[variant] || TEXT.contact;
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const currentCandidate = useSelector((state) => state.current_candidate);
  const currentRecruiter = useSelector((state) => state.current_recruiter);

  const isAuthenticated = currentCandidate || currentRecruiter;

  const [values, setValues] = useState({
    email: "",
    subject: "",
    content: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validate = (fieldValues = values) => {
    const temp = { ...errors };
    const required = intl.formatMessage({ id: "form_validation.required" });
    const not_an_email = intl.formatMessage({ id: "form_validation.not_an_email" });

    if ("email" in fieldValues && !isAuthenticated) {
      temp.email = fieldValues.email ? "" : required;
      if (fieldValues.email && !validator.isEmail(fieldValues.email))
        temp.email = not_an_email;
    }
    if ("subject" in fieldValues) {
      temp.subject = fieldValues.subject ? "" : required;
    }
    if ("content" in fieldValues) {
      temp.content = fieldValues.content ? "" : required;
    }

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    validate({ [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    if (validate(values)) {
      let params = {
        subject: values.subject,
        content: values.content,
      };
      if (!isAuthenticated) params["email"] = values.email;
      if (variant === "bug_report") {
        params["kind"] = "bug_report";
        params["page_url"] = document.referrer;
        params["user_agent"] = navigator.userAgent;
      }

      sh.post("contact-us", { data: params })
        .then(() => {
          setIsSuccess(true);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          enqueueSnackbar(
            intl.formatMessage({ id: "flash.unknown" }),
            { variant: "error" }
          );
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(230, 100%, 99%)", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: isDesktop ? "flex-start" : "center",
          justifyContent: "center",
          px: "1.5rem",
          pt: "1rem",
          pb: "1rem",
          minHeight: "calc(100vh - 68px)",
          overflow: "visible",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "64rem",
            alignItems: "center",
          }}
        >
          {/* ── Form card ── */}
          <Box
            sx={{
              width: isDesktop ? "50%" : "100%",
              bgcolor: "hsl(0, 0%, 100%)",
              borderRadius: "1rem",
              boxShadow:
                "0 4px 12px -2px rgb(0 0 0 / 0.06), 0 2px 4px -2px rgb(0 0 0 / 0.04)",
              p: isDesktop ? "2.5rem" : "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
              mt: isDesktop ? "-6rem" : 0,
            }}
          >
            {isSuccess ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2rem",
                  py: "2rem",
                }}
              >
                <img
                  src={MessageSent}
                  alt="Message sent"
                  width="240px"
                />
                <Typography
                  sx={{
                    fontFamily: "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
                    fontSize: isDesktop ? "1.875rem" : "1.5rem",
                    lineHeight: 1.2,
                    color: "hsl(222.2, 84%, 4.9%)",
                    textAlign: "center",
                  }}
                >
                  <FormattedMessage id={text.success} />
                </Typography>
              </Box>
            ) : (
              <>
                {/* Title */}
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "'Segoe UI Black', 'Segoe UI', system-ui, sans-serif",
                    fontSize: isDesktop ? "1.875rem" : "1.5rem",
                    lineHeight: 1.2,
                    color: "hsl(222.2, 84%, 4.9%)",
                    margin: 0,
                  }}
                >
                  <FormattedMessage id={text.title} />
                </Typography>

                {/* Subtitle */}
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "hsl(215.4, 16.3%, 46.9%)",
                    fontFamily: "'Segoe UI', system-ui, sans-serif",
                  }}
                >
                  <FormattedMessage id={text.subtitle} />
                </Typography>

                {/* Form */}
                <form
                  onSubmit={handleSubmit}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {!isAuthenticated && (
                    <TextField
                      fullWidth
                      required
                      type="email"
                      variant="outlined"
                      name="email"
                      onChange={handleInputValue}
                      onBlur={handleInputValue}
                      sx={inputSx}
                      {...(errors.email && {
                        error: true,
                        helperText: errors.email,
                      })}
                      label={<FormattedMessage id="devise.signup.email" />}
                    />
                  )}
                  <TextField
                    fullWidth
                    required
                    type="text"
                    variant="outlined"
                    name="subject"
                    onChange={handleInputValue}
                    onBlur={handleInputValue}
                    sx={inputSx}
                    {...(errors.subject && {
                      error: true,
                      helperText: errors.subject,
                    })}
                    label={
                      <FormattedMessage id="landing_candidate.contact_form.subject" />
                    }
                  />
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    variant="outlined"
                    name="content"
                    onChange={handleInputValue}
                    onBlur={handleInputValue}
                    sx={inputSx}
                    {...(errors.content && {
                      error: true,
                      helperText: errors.content,
                    })}
                    label={
                      <FormattedMessage id={text.messageLabel} />
                    }
                  />

                  {/* Submit */}
                  <Button
                    fullWidth
                    type="submit"
                    disabled={loading}
                    sx={{
                      bgcolor: "hsl(153 68% 25%)",
                      color: "white",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      px: "1.5rem",
                      py: "0.75rem",
                      borderRadius: "0.75rem",
                      textTransform: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      mt: "0.75rem",
                      "&:hover": {
                        bgcolor: "hsl(153 68% 25% / 0.9)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "hsl(153 68% 25% / 0.3)",
                        color: "hsl(0 0% 100% / 0.6)",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress
                        size={20}
                        sx={{ color: "white" }}
                      />
                    ) : (
                      <FormattedMessage id="send" />
                    )}
                  </Button>
                </form>
              </>
            )}
          </Box>

          {/* ── Crane illustration ── */}
          {isDesktop && (
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                overflow: "visible",
                position: "relative",
                zIndex: 1,
              }}
            >
              <img
                src={Crane}
                alt="Crane illustration"
                style={{
                  height: "100vh",
                  maxHeight: "880px",
                  objectFit: "contain",
                  transform: "translateX(48px) scale(0.9)",
                  transformOrigin: "top right",
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ContactUs;
